import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  HiChevronUp,
  HiChevronDown,
  HiMinus,
  HiOutlinePlusSm,
} from 'react-icons/hi';
import { isNil } from 'lodash';

import { Text, Button } from 'modules/common';
import { parseNumber } from 'modules/common/utils';

function FormInputNumber({
  name,
  trailingItem,
  label,
  error,
  leadingItem,
  required,
  className,
  onChange,
  errorClassName,
  labelClassName,
  onSetFieldValue,
  containerClassName,
  trailingType,
  buttonProps,
  leadingType,
  disabled,
  helperText,
  isOptional,
  stepper,
  width,
  value: $value,
  withHiddenLabel,
  ...rest
}) {
  const value = $value || '';
  const ref = React.useRef();

  const itemProps = {
    buttonProps: {
      disabled,
      size: 'xs',
    },
    iconProps: {
      className: 'h-4 w-4',
    },
  };

  const handleOnFocus = () => {
    try {
      ref.current.focus();
    } catch (err) {
      // do_nothing
    }
  };

  const handleOnChange = (e) => ($v) => {
    let v = !isNil($v) ? `${$v}` : e?.target?.value;

    // firefox issue handler
    v = v?.replace(/[a-z]|[A-Z]/g, '');

    // // firefox issue handler
    if (!/^[0-9]*\.?[0-9]*$/.test(v)) {
      return;
    }

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, v);
      return;
    }

    if (typeof onChange === 'function') {
      onChange((prev) => ({
        ...prev,
        [name]: v,
      }));
    }
  };

  const handleKeyDown = (e) => {
    const key = e?.charCode || e?.key;

    if (key === '.' && value?.length <= 0) {
      e.preventDefault();
    }

    if ((key === '-' || key === 'e') && `${rest?.min}` === '0') {
      e.preventDefault();
    }
  };

  const handleOnIncrement = () => {
    if (disabled) return;

    handleOnChange()(parseNumber(value ?? 0) + 1);
  };

  const handleOnDecrement = () => {
    if (disabled) return;

    handleOnChange()(parseNumber(value ?? 0) - 1);
  };

  const dispatchItem = {
    arrows: (
      <div
        className={cn(
          'flex flex-col text-muted divide-y border-l border-default rounded-r -m-ds-12 h-[40px]',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          trailingType === 'arrows' ? '!ml-0' : '!mr-0'
        )}
      >
        <div
          className="flex flex-1 px-ds-8 bg-light hover:bg-dark rounded-tr border-default"
          onClick={handleOnIncrement}
          role="presentation"
        >
          <HiChevronUp className="h-4 w-4 m-auto" />
        </div>

        <div
          className="flex flex-1 px-ds-8 bg-light hover:bg-dark rounded-br border-default"
          onClick={handleOnDecrement}
          role="presentation"
        >
          <HiChevronDown className="h-4 w-4 m-auto" />
        </div>
      </div>
    ),
    button: (
      <div className="my-[-5px] -mx-ds-4">
        <Button size="xs" {...buttonProps} />
      </div>
    ),
    'default-button': (
      <Button
        {...buttonProps}
        className={cn(
          '-m-ds-12',
          trailingType === 'default-button'
            ? 'border-t-0 border-r-0 border-b-0 !ml-0'
            : 'border-t-0 border-l-0 border-b-0 !mr-0',
          buttonProps?.className
        )}
      />
    ),
  };

  if (stepper === 'horizontal') {
    return (
      <FormInputNumberSmall
        error={error}
        onFocus={handleOnFocus}
        label={label}
        required={required}
        isOptional={isOptional}
        labelClassName={labelClassName}
        id={rest?.id}
        onKeyDown={handleKeyDown}
        onChange={handleOnChange}
        onIncrement={handleOnIncrement}
        onDecrement={handleOnDecrement}
        disabled={disabled}
        helperText={helperText}
        errorClassName={errorClassName}
        name={name}
        {...rest}
        width={width}
      />
    );
  }

  return (
    <div className="w-full">
      <div className="w-full relative">
        <div
          className={cn(
            'form-container transition duration-300 ease-in-out',
            containerClassName,
            {
              'form-container': !error,
              'form-container-error': error,
            }
          )}
          onClick={handleOnFocus}
          role="presentation"
        >
          {withHiddenLabel ? (
            <div className="ds-form-label opacity-0">RO</div>
          ) : (
            <>
              {label && (
                <label
                  className={cn(
                    'ds-form-label flex items-center',
                    labelClassName
                  )}
                  onClick={handleOnFocus}
                  role="presentation"
                >
                  <div className="flex-1">
                    {label}{' '}
                    {required ? (
                      <span className="text-utility-danger-base">*</span>
                    ) : (
                      ''
                    )}
                  </div>
                  {isOptional && <div className="font-normal">Optional</div>}
                </label>
              )}
            </>
          )}

          <div
            className={cn(
              'transition duration-300 ease-in-out relative gap-[10px]',
              {
                'ds-form-input': !error,
                'ds-form-input-error': error,
                'ds-form-input-disabled': disabled,
              }
            )}
          >
            {Boolean(leadingItem || dispatchItem?.[leadingType]) && (
              <div
                className={cn(
                  'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                  {
                    'form-icon': !disabled,
                    'form-icon-disabled': disabled,
                  }
                )}
              >
                {leadingItem ? (
                  <>
                    {typeof leadingItem === 'function'
                      ? leadingItem(itemProps)
                      : leadingItem}
                  </>
                ) : (
                  dispatchItem?.[leadingType]
                )}
              </div>
            )}

            <input
              id={rest?.id || name}
              type="number"
              autoComplete="off"
              name={name}
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                handleOnChange(e)();
              }}
              className={cn(
                'w-full flex items-center disabled:cursor-not-allowed h-[16px] transition-all duration-300 ease-in-out',
                !disabled ? 'placeholder-placeholder' : 'placeholder-disabled',
                className,
                { 'bg-utility-danger-muted': error }
              )}
              disabled={disabled}
              value={value}
              {...rest}
              style={{ outline: 'none', ...rest?.style }}
              ref={ref}
            />

            {Boolean(trailingItem || dispatchItem?.[trailingType]) && (
              <div
                className={cn(
                  'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                  {
                    'form-icon': !disabled,
                    'form-icon-disabled': disabled,
                  }
                )}
              >
                {trailingItem ? (
                  <>
                    {typeof trailingItem === 'function'
                      ? trailingItem(itemProps)
                      : trailingItem}
                  </>
                ) : (
                  dispatchItem?.[trailingType]
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {(Boolean(error) || Boolean(helperText)) && (
        <Text
          type="caption"
          className={cn(
            'mt-ds-6',
            {
              'ds-form-helper': !error,
              'ds-form-helper-error': error,
              'ds-form-helper-disabled': disabled,
            },
            errorClassName
          )}
        >
          <span>{error || helperText || ''}</span>
          <span className="form-error-badge" />
        </Text>
      )}
    </div>
  );
}

FormInputNumber.defaultProps = {
  label: 'Name',
  containerClassName: '',
  labelClassName: '',
  errorClassName: '',
  className: '',
  required: false,
  type: 'number',
  error: false,
  trailingItem: false,
  leadingItem: false,
  onSetFieldValue: false,
  onChange: false,
  disabled: false,
  isOptional: false,
  withHiddenLabel: false,
  helperText: '',
  stepper: 'vertical',
  width: '120px',
  trailingType: '',
  leadingType: '',
  buttonProps: {},
  value: '',
};

FormInputNumber.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  stepper: PropTypes.oneOf(['vertical', 'horizontal']),
  name: PropTypes.string.isRequired,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  isOptional: PropTypes.bool,
  helperText: PropTypes.string,
  labelClassName: PropTypes.string,
  errorClassName: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  width: PropTypes.string,
  required: PropTypes.bool,
  withHiddenLabel: PropTypes.bool,
  trailingItem: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  onChange: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  buttonProps: PropTypes.instanceOf(Object),
  leadingType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['password', 'search', 'button']),
  ]),
  trailingType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(['password', 'search', 'button']),
  ]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default React.memo(FormInputNumber);

function FormInputNumberSmall(
  error,
  label,
  name,
  required,
  isOptional,
  labelClassName,
  id,
  onKeyDown,
  onChange,
  disabled,
  onDecrement,
  onIncrement,
  errorClassName,
  helperText,
  width,
  value,
  withHiddenLabel,
  ...rest
) {
  const ref = React.useRef();

  const handleOnFocus = () => {
    try {
      ref.current.focus();
    } catch (err) {
      // do_nothing
    }
  };

  return (
    <div>
      <div className="relative w-full flex">
        <div
          className={cn('form-container transition duration-300 ease-in-out', {
            'form-container': !error,
            'form-container-error': error,
          })}
          onClick={handleOnFocus}
          role="presentation"
        >
          {withHiddenLabel ? (
            <div className="ds-form-label opacity-0">RO</div>
          ) : (
            <>
              {label && (
                <label
                  className={cn(
                    'ds-form-label flex items-center',
                    labelClassName
                  )}
                  htmlFor={id || name}
                >
                  <div className="flex-1">
                    {label}{' '}
                    {required ? (
                      <span className="text-utility-danger-base">*</span>
                    ) : (
                      ''
                    )}
                  </div>
                  {isOptional && <div className="font-normal">Optional</div>}
                </label>
              )}
            </>
          )}

          <div className="inline-flex relative w-full">
            <div
              className={cn(
                'absolute inset-px right-auto px-ds-12 flex items-center rounded-l',
                disabled
                  ? 'cursor-not-allowed text-disabled'
                  : 'cursor-pointer hover:bg-light text-brand-primary-base'
              )}
              onClick={onDecrement}
              role="presentation"
            >
              <HiMinus className="h-4 w-4" />
            </div>

            <input
              id={id || name}
              type="number"
              autoComplete="off"
              name={name}
              onKeyDown={onKeyDown}
              onChange={(e) => {
                onChange(e)();
              }}
              className={cn(
                'transition duration-300 ease-in-out text-center px-11',
                {
                  'ds-form-input': !error,
                  'ds-form-input-error': error,
                  'ds-form-input-disabled': disabled,
                }
              )}
              disabled={disabled}
              value={value}
              {...rest}
              ref={ref}
              placeholder="0"
              style={{
                width,
              }}
            />

            <div
              className={cn(
                'absolute inset-px left-auto px-ds-12 flex items-center rounded-r',
                disabled
                  ? 'cursor-not-allowed text-disabled'
                  : 'cursor-pointer hover:bg-light text-brand-primary-base'
              )}
              onClick={onIncrement}
              role="presentation"
            >
              <HiOutlinePlusSm className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>

      {(Boolean(error) || Boolean(helperText)) && (
        <Text
          type="caption"
          className={cn(
            'mt-ds-6',
            {
              'ds-form-helper': !error,
              'ds-form-helper-error': error,
              'ds-form-helper-disabled': disabled,
            },
            errorClassName
          )}
        >
          <span>{error || helperText || ''}</span>
          <span className="form-error-badge" />
        </Text>
      )}
    </div>
  );
}

FormInputNumberSmall.defaultProps = {
  error: false,
  label: 'Name',
  required: false,
  isOptional: false,
  labelClassName: '',
  id: '',
  onKeyDown: () => {},
  onChange: () => {},
  onIncrement: () => {},
  onDecrement: () => {},
  disabled: false,
  withHiddenLabel: false,
  helperText: '',
  errorClassName: '',
  width: '120px',
  value: '',
};

FormInputNumberSmall.propTypes = {
  name: PropTypes.string.isRequired,
  labelClassName: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  isOptional: PropTypes.bool,
  width: PropTypes.string,
  required: PropTypes.bool,
  withHiddenLabel: PropTypes.bool,
  id: PropTypes.string,
  helperText: PropTypes.string,
  errorClassName: PropTypes.string,
  onKeyDown: PropTypes.instanceOf(Function),
  onChange: PropTypes.instanceOf(Function),
  onIncrement: PropTypes.instanceOf(Function),
  onDecrement: PropTypes.instanceOf(Function),
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  value: PropTypes.string,
};
